'use client';

import { Box, VisuallyHidden, keyframes } from '@chakra-ui/react';
interface ComponentSpinnerProps {
  children: React.ReactNode;
  size?: string;
  inner?: string;
  border?: string;
  duration?: string;
  minHeight?: string;
}
const spinKeyframes = keyframes`
    0% { transform: rotate(0deg); }
    30% { transform: rotate(90deg); }
    45% { transform: rotate(180deg); }
    65% { transform: rotate(270deg); }
    100% { transform: rotate(360deg); }
`;
/**
 * Client side JSX component, V2 SpinnerModal.
 * Model is always open, for simplicity, client side components use conditional rendering to show modal.
 * @param {string} id       (default = enroly)
 * @param {string} size     (default = 96px)
 * @param {string} inner    (default = 0.5em)
 * @param {string} border   (default = 0.125em)
 * @param {string} duration (default = 1.25s)
 * @returns {JSX.Element} JSX.Element
 */
export default function ComponentSpinner({
  children,
  size = '64px',
  inner = '0.5em',
  border = '0.125em',
  duration = '1.25s',
  minHeight = '300px'
}: ComponentSpinnerProps) {
  const pseudoCommon = {
    content: '""',
    display: 'block',
    height: inner,
    width: inner,
    position: 'absolute',
    borderRadius: 'full',
    borderStyle: 'solid',
    borderWidth: border
  };
  return (
    <Box
      id="component_spinner"
      bgColor="chakra-subtle-bg"
      borderRadius="2xl"
      mb="24px"
      position="relative"
      margin={-5}
      minHeight={minHeight}
    >
      <Box
        position="absolute"
        zIndex={2}
        top={0}
        bottom={0}
        left={0}
        right={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        backdropFilter={'blur(3px)'}
      >
        <Box
          bgColor="utility.900"
          borderRadius="full"
          color="white"
          display="inline-flex"
          fontSize={size}
          height="1em"
          width="1em"
          position="relative"
          alignItems="center"
          justifyContent="center"
          _before={{
            ...pseudoCommon,
            borderColor: 'whiteAlpha.200'
          }}
          _after={{
            ...pseudoCommon,
            borderColor: 'transparent',
            borderTopColor: 'yellow.500',
            animation: `2.5s ${spinKeyframes} linear infinite`,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: `${duration} ${spinKeyframes} linear infinite`
            }
          }}
        >
          <VisuallyHidden>Loading...</VisuallyHidden>
        </Box>
      </Box>
      <Box position={'relative'} overflow={'hidden'} margin={+5}>
        {children}
      </Box>
    </Box>
  );
}
